import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { LocalizableMessage } from 'components/LocalizableMessage/LocalizableMessage';
import { StarRating } from 'components/StarRating/StarRating';
import { DESIGN_SYSTEM_ICONS } from 'constants/icons';
import { isEmpty } from 'helpers/verify';
import { Image } from 'components/Image/Image';
import { Heading } from 'components/Heading/Heading';
import { Section } from 'components/Section/Section';
import { Image as ImageModel, RES } from 'models/Image';
import { messages } from './VenueItem.messages';

const formatAddress = address => {
  if (address) {
    const streetAddressComponents = [address.address_line0, address.address_line1, address.address_line2].filter(
      line => !!line
    );

    if (streetAddressComponents.length > 0) {
      const streetAddress = streetAddressComponents.join(', ');
      const { city } = address;
      return city ? `${streetAddress}, ${address.city}` : streetAddress;
    }
  }

  return null;
};

export const VenueItem = ({
  className,
  description,
  name,
  alias,
  locationName,
  activities,
  rating,
  ratingCount,
  ratingMessage,
  subtitle,
  images,
  address,
  excludeDescription,
  excludeFeaturedReview,
  featuredReviews,
  searchId,
}) => {
  const [isMoreInfoHidden, setMoreInfoHidden] = useState(true);
  const [isReviewHidden, setReviewHidden] = useState(true);

  const handleToggleMoreInfoHidden = () => {
    setMoreInfoHidden(!isMoreInfoHidden);
  };

  const handleToggleReview = () => {
    setReviewHidden(!isReviewHidden);
  };

  const featuredReview = !isEmpty(featuredReviews) && featuredReviews[0];
  const isReviewAvailable = !excludeFeaturedReview && !!featuredReview;
  const venueDetailPath = `/studios/${alias}?search-id=${searchId}`;
  const venueDetailReviewsPath = `${venueDetailPath}#reviews`;
  const venueName = `${name}${subtitle ? ` · ${subtitle}` : ''}`;

  const reviewContent = (
    <div className="venue-item__review">
      <p className="venue-item__review__title">{featuredReview.class_name}</p>
      <div className="venue-item__review__content">
        <q>{featuredReview.review}</q>
      </div>
      <a
        className="venue-item__review__see-all"
        href={venueDetailReviewsPath}
        target="_blank"
        rel="noopener noreferrer">
        <LocalizableMessage message={messages.seeAllReviews} />
      </a>
    </div>
  );

  const moreInfoToggleMessage = isMoreInfoHidden ? messages.moreInfo : messages.lessInfo;
  const locationInfo = formatAddress(address) || locationName;

  return (
    <Section className={classNames('venue-item', className)}>
      <a href={venueDetailPath} target="_blank" rel="noopener noreferrer" className="venue-item__image">
        <Image alt={venueName} url={ImageModel.getImage(images, RES.low)} />
        <div className="venue-item__image__border" />
      </a>
      <div className="venue-item__highlights">
        {activities && <div className="venue-item__activities text--ellipsis">{activities}</div>}
        <Heading className="zeta text--ellipsis">
          <a href={venueDetailPath} target="_blank" rel="noopener noreferrer" className="venue-item__name">
            {venueName}
          </a>
        </Heading>
        {locationInfo && <div className="venue-item__location text--ellipsis">{locationInfo}</div>}
        <div className="venue-item__rating" onMouseOut={handleToggleReview} onMouseOver={handleToggleReview}>
          {isReviewAvailable && (
            <div className={classNames('venue-item__tooltip', { hidden: isReviewHidden })}>
              <div className="venue-item__tooltip__content">{reviewContent}</div>
            </div>
          )}
          {!ratingMessage && (
            <a href={venueDetailReviewsPath} target="_blank" rel="noopener noreferrer">
              <StarRating
                className="venue-item__rating__star"
                rating={rating}
                count={ratingCount}
                showRatingNumber
                singleStar
              />
            </a>
          )}
          {isReviewAvailable && (
            <Isvg className="venue-item__show-review-prompt" src={DESIGN_SYSTEM_ICONS.arrowDownSmall} />
          )}
          {!excludeDescription && (
            <a className="venue-item__toggle-more-info text--ellipsis" onClick={handleToggleMoreInfoHidden}>
              <LocalizableMessage message={moreInfoToggleMessage} />
            </a>
          )}
        </div>
      </div>
      {!excludeDescription && description && (
        <div
          className={classNames('venue-item__more-info', {
            'venue-item__more-info--hidden-mobile': isMoreInfoHidden,
          })}>
          {isReviewAvailable && <div className="venue-item__inline-review">{reviewContent}</div>}
          <div className="venue-item__description">{description}</div>
        </div>
      )}
    </Section>
  );
};

VenueItem.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  alias: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  activities: PropTypes.string,
  locationName: PropTypes.string,
  rating: PropTypes.number,
  ratingCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ratingMessage: PropTypes.string,
  subtitle: PropTypes.string,
  images: PropTypes.object.isRequired,
  address: PropTypes.shape({
    address_line_0: PropTypes.string,
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    city: PropTypes.string,
  }),
  excludeDescription: PropTypes.bool,
  featuredReviews: PropTypes.arrayOf(
    PropTypes.shape({
      class_name: PropTypes.string.isRequired,
      review: PropTypes.string.isRequired,
    })
  ),
  excludeFeaturedReview: PropTypes.bool,
  searchId: PropTypes.string,
};
